import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';

const NAV = [
  { id: 'dashboard',  icon: '📊', label: 'Dashboard'                                    },
  { id: 'assets',     icon: '📦', label: 'Assets',      feature: 'can_assets'           },
  { id: 'facilities', icon: '🏭', label: 'Facilities',  feature: 'can_facilities'       },
  { id: 'checkout',   icon: '🔄', label: 'Checkout',    feature: 'can_checkout',   badge: 'checkout'   },
  { id: 'requests',   icon: '📋', label: 'Requests',    feature: 'can_requests',   badge: 'requests'   },
  { id: 'deliveries', icon: '🚚', label: 'Deliveries',  feature: 'can_deliveries', badge: 'deliveries' },
  { id: 'tracking',   icon: '📡', label: 'Live Tracking', feature: 'can_realtime'        },
  { id: 'reports',    icon: '📈', label: 'Reports',     feature: 'can_reports'          },
];

const NAV_BOTTOM = [
  { id: 'users',    icon: '👥', label: 'Users',    perm: 'canManageUsers' },
  { id: 'settings', icon: '⚙',  label: 'Settings', perm: 'canConfig'     },
];

export default function Sidebar({ page, setPage, perms, badges, isOpen, onClose }) {
  const { signOut, profile } = useAuth();
  const { org, features } = useTenant();
  const companyName   = org?.name || 'RFID WMS';
  const platformName  = localStorage.getItem('rfid_wms_platform_name') || 'RFID WMS';
  const brandLogo = localStorage.getItem('rfid_wms_brand_logo') || '';

  // Filter nav items by package features
  const visibleNav = NAV.filter(item => {
    if (!item.feature) return true;          // no feature gate = always show
    if (!features) return true;              // features not loaded yet = show all
    return features[item.feature] !== false; // hide only if explicitly false
  });

  const NavItem = ({ item }) => {
    const active = page === item.id;
    const count  = item.badge ? badges[item.badge] : 0;
    return (
      <button
        onClick={() => setPage(item.id)}
        style={{
          display: 'flex', alignItems: 'center', gap: 10,
          width: '100%', padding: '10px 14px', borderRadius: 8,
          border: 'none', background: active ? '#1a2a3a' : 'none',
          color: active ? '#00e5a0' : '#a0b4c0',
          cursor: 'pointer', fontSize: 13, fontWeight: 600,
          fontFamily: "'IBM Plex Sans',sans-serif",
          transition: 'all 0.15s', textAlign: 'left',
          position: 'relative',
        }}
        onMouseEnter={e => !active && (e.currentTarget.style.color = '#c8d6e0')}
        onMouseLeave={e => !active && (e.currentTarget.style.color = '#a0b4c0')}
      >
        <span style={{ fontSize: 16, width: 20, textAlign: 'center' }}>{item.icon}</span>
        <span style={{ flex: 1 }}>{item.label}</span>
        {count > 0 && (
          <span style={{
            background: '#f5a623', color: '#060b10',
            fontSize: 10, fontWeight: 800,
            padding: '1px 6px', borderRadius: 10, minWidth: 18, textAlign: 'center',
          }}>{count}</span>
        )}
        {active && <span style={{ position: 'absolute', right: 0, top: '20%', height: '60%', width: 3, background: '#00e5a0', borderRadius: '3px 0 0 3px' }} />}
      </button>
    );
  };

  return (
    <div
      className={`sidebar-drawer ${isOpen ? 'open' : ''}`}
      style={{
        width: 220, flexShrink: 0, background: '#0a0f16',
        borderRight: '1px solid #1a2a3a', display: 'flex', flexDirection: 'column',
        height: '100vh', overflow: 'hidden',
      }}
    >
      {/* Logo + mobile close */}
      <div style={{ padding: '20px 18px 16px', borderBottom: '1px solid #1a2a3a', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ minWidth: 0, flex: 1, marginRight: 8 }}>
          {brandLogo ? (
            <img src={brandLogo} alt="Logo"
              style={{ height: 44, maxWidth: 180, objectFit: 'contain', display: 'block' }}
              onError={e => e.target.style.display='none'}/>
          ) : (
            <>
              <div style={{
                fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 15,
                color: '#e8edf2', letterSpacing: '-0.3px',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {platformName}
              </div>
              <div style={{ fontSize: 10, color: '#7090a0', marginTop: 3, fontFamily: "'IBM Plex Mono',monospace" }}>
                Warehouse Management System
              </div>
            </>
          )}
        </div>
        <button className="hamburger-btn" onClick={onClose} style={{
          background: 'none', border: '1px solid #1a2a3a', borderRadius: 6,
          color: '#a0b4c0', fontSize: 16, cursor: 'pointer', padding: '4px 9px',
          lineHeight: 1, flexShrink: 0,
        }}>✕</button>
      </div>

      {/* Main nav */}
      <nav style={{ flex: 1, padding: '12px 10px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {visibleNav.map(item => <NavItem key={item.id} item={item} />)}
        <div style={{ height: 1, background: '#1a2a3a', margin: '10px 0' }} />
        {NAV_BOTTOM.map(item =>
          perms[item.perm] && <NavItem key={item.id} item={item} />
        )}
      </nav>

      {/* User profile */}
      <div style={{ padding: '14px 16px', borderTop: '1px solid #1a2a3a' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <div style={{
            width: 32, height: 32, borderRadius: '50%',
            background: '#00e5a018', border: '1.5px solid #00e5a030',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 14, flexShrink: 0,
          }}>
            {(profile?.name || 'U').charAt(0).toUpperCase()}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#c8d6e0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {profile?.name || 'User'}
            </div>
            <div style={{ fontSize: 10, color: '#7090a0' }}>{profile?.role || 'Viewer'}</div>
          </div>
        </div>
        <button onClick={signOut} style={{
          width: '100%', padding: '7px 0', borderRadius: 7,
          background: '#e0575712', border: '1px solid #e0575730',
          color: '#e05757', fontSize: 11, fontWeight: 600,
          cursor: 'pointer', fontFamily: "'IBM Plex Sans',sans-serif",
        }}>Sign Out</button>
      </div>
    </div>
  );
}
